import { useListPagination } from '@/hooks/useListPagination'
import { ListPagination } from '@/components/ui/list-pagination'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useOrdini, useDeleteOrdine } from '@/hooks/useOrdini'
import { useVenditoriTutti } from '@/hooks/useVenditori'
import { getStatisticheOrdini } from '@/api/ordini'
import { ordiniLiveQueryOptions } from '@/config/queryClient'
import { useSortingState } from '@/hooks/useSortingState'
import { SortableTableHead } from '@/components/ui/sortable-table-head'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ShoppingCart, Search, Plus, Eye, Pencil, Trash2, Truck, CheckCircle, AlertTriangle, XCircle, Gift, Archive } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'

const STATO_COLORS = {
  bozza: 'secondary',
  confermato: 'default',
  in_preparazione: 'warning',
  preparato: 'warning',
  parzialmente_spedito: 'default',
  spedito: 'default',
  evaso: 'success',
  chiuso: 'secondary',
  annullato: 'destructive',
}

const TIPO_LABELS = {
  1: 'Normale',
  2: 'Contenuto Pers.',
  3: 'Brandizzazione',
  4: 'Completo',
}

export default function OrdiniList() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [stato, setStato] = useState('')
  const [tipo, setTipo] = useState('')
  const [statoPagamento, setStatoPagamento] = useState('')
  const [venditore, setVenditore] = useState('')
  const [ordering, setOrdering] = useSortingState('ordini', '-data_ordine')
  const { data: venditori = [] } = useVenditoriTutti()
  
  const filtriStats = {
    search: search || undefined,
    stato: stato || undefined,
    tipo: tipo || undefined,
    stato_pagamento: statoPagamento || undefined,
    venditore: venditore || undefined,
  }
  const { page, setPage } = useListPagination({ ...filtriStats, ordering })

  const { data: ordini, isLoading, isFetching, error } = useOrdini({
    page,
    ...filtriStats,
    ordering,
  })
  const { data: statsData } = useQuery({
    queryKey: ['ordini', 'statistiche', filtriStats],
    queryFn: () => getStatisticheOrdini(filtriStats).then((r) => r.data),
    ...ordiniLiveQueryOptions,
  })
  const deleteMutation = useDeleteOrdine()

  // Dialog custom con digitazione codice (Punto 17 feedback cliente)
  const [deleteDialog, setDeleteDialog] = useState({ open: false, ordine: null })
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('')

  const openDeleteDialog = (ordine) => {
    setDeleteConfirmInput('')
    setDeleteDialog({ open: true, ordine })
  }
  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, ordine: null })
    setDeleteConfirmInput('')
  }
  const handleConfirmDelete = () => {
    if (!deleteDialog.ordine) return
    if (deleteConfirmInput.trim() !== deleteDialog.ordine.numero) return
    deleteMutation.mutate(deleteDialog.ordine.id, {
      onSuccess: () => closeDeleteDialog(),
    })
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(value || 0)
  }

  const formatCurrencyCents = (value) => formatCurrency((value || 0) / 100)

  const renderSpedizioneIcon = (spedizioneStatus) => {
    if (spedizioneStatus === 'completo') {
      return (
        <div className="flex items-center justify-center" title="Completamente spedito">
          <Truck className="h-5 w-5 text-green-600" />
        </div>
      )
    } else if (spedizioneStatus === 'parziale') {
      return (
        <div className="flex items-center justify-center" title="Parzialmente spedito">
          <Truck className="h-5 w-5 text-yellow-600" />
        </div>
      )
    }
    return null
  }

  const renderPagamentoBadge = (ordine) => {
    const totalePagato = formatCurrencyCents(ordine.totale_pagato_cents)
    const scontiPagamento = formatCurrencyCents(ordine.sconti_pagamento_cents)
    const totaleOrdine = formatCurrency(ordine.totale)
    const titoloDettaglio = `Incassato ${totalePagato}, sconti ${scontiPagamento} / totale ${totaleOrdine}`

    if (ordine.stato_pagamento === 'omaggio') {
      return (
        <Badge variant="success" title="Ordine omaggio">
          <Gift className="h-3.5 w-3.5 mr-1" />
          Omaggio
        </Badge>
      )
    }
    if (ordine.stato_pagamento === 'pagato') {
      return (
        <Badge variant="success" title={`Ordine saldato (${titoloDettaglio})`}>
          <CheckCircle className="h-3.5 w-3.5 mr-1" />
          {ordine.sconti_pagamento_cents > 0 || ordine.totale_cents === 0 ? 'Saldato' : 'Pagato'}
        </Badge>
      )
    }
    if (ordine.stato_pagamento === 'parziale') {
      return (
        <Badge variant="warning" title={`Parzialmente pagato (${titoloDettaglio})`}>
          <AlertTriangle className="h-3.5 w-3.5 mr-1" />
          Parziale
        </Badge>
      )
    }

    return (
      <Badge variant="destructive" title={`Non pagato (${titoloDettaglio})`}>
        <XCircle className="h-3.5 w-3.5 mr-1" />
        Non pagato
      </Badge>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Ordini</h1>
          <p className="text-muted-foreground">Gestione ordini clienti</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/ordini/archivio')}>
            <Archive className="mr-2 h-4 w-4" />
            Archivio
          </Button>
          <Button onClick={() => navigate('/ordini/nuovo')}>
            <Plus className="mr-2 h-4 w-4" />
            Nuovo Ordine
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Lista Ordini
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca per numero, cliente, referente aziendale o venditore..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              aria-label="Filtra per venditore"
              value={venditore}
              onChange={(e) => setVenditore(e.target.value)}
              className="border rounded-md px-3 min-h-10"
            >
              <option value="">Tutti i venditori</option>
              {venditori.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.nome_completo || `${item.nome} ${item.cognome}`}{item.attivo ? '' : ' (inattivo)'}
                </option>
              ))}
            </select>
            <select
              value={stato}
              onChange={(e) => setStato(e.target.value)}
              className="border rounded-md px-3"
            >
              <option value="">Tutti gli stati</option>
              <option value="bozza">Bozza</option>
              <option value="confermato">Confermato</option>
              <option value="in_preparazione">In Preparazione</option>
              <option value="preparato">Preparato</option>
              <option value="spedito">Spedito</option>
              <option value="evaso">Evaso</option>
              <option value="chiuso">Chiuso</option>
              <option value="annullato">Annullato</option>
            </select>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="border rounded-md px-3"
            >
              <option value="">Tutti i tipi</option>
              <option value="1">Normale</option>
              <option value="2">Contenuto Personalizzato</option>
              <option value="3">Brandizzazione</option>
              <option value="4">Completo</option>
            </select>
            <select
              value={statoPagamento}
              onChange={(e) => setStatoPagamento(e.target.value)}
              className="border rounded-md px-3"
            >
              <option value="">Tutti i pagamenti</option>
              <option value="non_pagato">Non pagato</option>
              <option value="parziale">Parziale</option>
              <option value="pagato">Pagato</option>
              <option value="omaggio">Omaggio</option>
            </select>
          </div>

          {isLoading ? (
            <div className="text-center py-8">Caricamento...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead field="numero" currentOrdering={ordering} onSort={setOrdering}>
                    Numero
                  </SortableTableHead>
                  <SortableTableHead field="cliente_nome" currentOrdering={ordering} onSort={setOrdering}>Cliente</SortableTableHead>
                  <SortableTableHead field="venditore_nome" currentOrdering={ordering} onSort={setOrdering}>Venditore</SortableTableHead>
                  <SortableTableHead field="tipo" currentOrdering={ordering} onSort={setOrdering}>Tipo</SortableTableHead>
                  <SortableTableHead field="stato" currentOrdering={ordering} onSort={setOrdering}>
                    Stato
                  </SortableTableHead>
                  <SortableTableHead field="spedizione_status" currentOrdering={ordering} onSort={setOrdering} className="text-center w-12">Sped.</SortableTableHead>
                  <SortableTableHead field="data_ordine" currentOrdering={ordering} onSort={setOrdering}>
                    Data
                  </SortableTableHead>
                  <SortableTableHead field="data_consegna_richiesta_dal" currentOrdering={ordering} onSort={setOrdering}>
                    Consegna Dal
                  </SortableTableHead>
                  <SortableTableHead field="data_consegna_richiesta_al" currentOrdering={ordering} onSort={setOrdering}>
                    Consegna Al
                  </SortableTableHead>
                  <SortableTableHead field="totale_cents" currentOrdering={ordering} onSort={setOrdering} className="text-right">
                    Totale
                  </SortableTableHead>
                  <SortableTableHead field="stato_pagamento" currentOrdering={ordering} onSort={setOrdering}>
                    Pagamento
                  </SortableTableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ordini?.results?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                      Nessun ordine trovato
                    </TableCell>
                  </TableRow>
                ) : (
                  ordini?.results?.map((ordine) => (
                    <TableRow key={ordine.id}>
                      <TableCell className="font-mono font-medium">{ordine.numero}</TableCell>
                      <TableCell>{ordine.cliente_nome}</TableCell>
                      <TableCell className="text-muted-foreground">{ordine.venditore_nome || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {TIPO_LABELS[ordine.tipo] || ordine.tipo_display}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATO_COLORS[ordine.stato] || 'secondary'}>
                          {ordine.stato_display}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {renderSpedizioneIcon(ordine.spedizione_status)}
                      </TableCell>
                      <TableCell>{formatDate(ordine.data_ordine)}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatDate(ordine.data_consegna_richiesta_dal)}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatDate(ordine.data_consegna_richiesta_al)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(ordine.totale)}
                      </TableCell>
                      <TableCell>
                        {renderPagamentoBadge(ordine)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/ordini/${ordine.id}`)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {ordine.stato === 'bozza' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/ordini/${ordine.id}/modifica`)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDeleteDialog(ordine)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}

          {/* Punto 8 feedback cliente: footer riepilogativo (rispetta i filtri attivi) */}
          {statsData && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground uppercase">Valore vendita ordini</p>
                <p className="text-xl font-semibold tabular-nums">
                  {formatCurrencyCents(statsData.totale_vendite_cents)}
                </p>
                <p className="text-xs text-muted-foreground">Al netto di tutti gli sconti</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground uppercase">Costo stimato ordini</p>
                <p className="text-xl font-semibold tabular-nums">
                  {formatCurrencyCents(statsData.costo_stimato_cents)}
                </p>
                <p className="text-xs text-muted-foreground">Ai costi attuali di acquisto e composizione</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground uppercase">Sconti concessi</p>
                <p className="text-xl font-semibold tabular-nums text-emerald-700">
                  {formatCurrencyCents(statsData.totale_sconti_cents)}
                </p>
                <p className="text-xs text-muted-foreground">Su ordini e registrati come sconto</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground uppercase">Incassato</p>
                <p className="text-xl font-semibold tabular-nums text-green-600">
                  {formatCurrencyCents(statsData.totale_pagato_cents)}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground uppercase">Da ricevere</p>
                <p className="text-xl font-semibold tabular-nums text-amber-600">
                  {formatCurrencyCents(statsData.da_ricevere_cents)}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog eliminazione ordine con digitazione codice (Punto 17 feedback cliente) */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archivia ordine {deleteDialog.ordine?.numero}</DialogTitle>
            <DialogDescription>
              Questa azione archivier&agrave; l&apos;ordine. Potrai ripristinarlo dalla
              pagina &quot;Archivio&quot;. Per confermare, digita il codice ordine qui sotto.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="delete_confirm_code">
              Codice ordine: <span className="font-mono">{deleteDialog.ordine?.numero}</span>
            </Label>
            <Input
              id="delete_confirm_code"
              autoFocus
              placeholder={deleteDialog.ordine?.numero}
              value={deleteConfirmInput}
              onChange={(e) => setDeleteConfirmInput(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDeleteDialog}>
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={
                deleteConfirmInput.trim() !== deleteDialog.ordine?.numero ||
                deleteMutation.isPending
              }
            >
              {deleteMutation.isPending ? 'Archiviazione...' : 'Archivia ordine'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ListPagination data={ordini} page={page} setPage={setPage} isFetching={isFetching} error={error} />
    </div>
  )
}
